import React, { useState, useRef, useEffect } from 'react';
import { sendCoachMessage } from '../api';

const SUGGESTIONS = [
  'How am I doing this month?',
  'Should I pay off debt or save more?',
  'Where can I cut spending?',
  'What should I focus on next?',
];

export default function CoachPanel() {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, open, loading]);

  // Generate greeting on first open
  useEffect(() => {
    if (!open || initialized) return;
    setInitialized(true);
    setLoading(true);
    sendCoachMessage('__init__', []).then((res) => {
      setLoading(false);
      if (res.message) {
        setHistory([{ role: 'assistant', content: res.message }]);
      } else {
        setError(res.error || 'Could not connect to coach.');
      }
    });
  }, [open, initialized]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async (text) => {
    const msg = (text !== undefined ? text : input).trim();
    if (!msg || loading) return;
    setInput('');
    setError('');
    const newHistory = [...history, { role: 'user', content: msg }];
    setHistory(newHistory);
    setLoading(true);
    const res = await sendCoachMessage(msg, history);
    setLoading(false);
    if (res.message) {
      setHistory((h) => [...h, { role: 'assistant', content: res.message }]);
    } else {
      setError(res.error || 'Something went wrong.');
      setHistory((h) => h.slice(0, -1)); // remove the user message on hard error
    }
  };

  const handleToggle = () => {
    setOpen((o) => !o);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleToggle}
        className="coach-fab"
        title={open ? 'Close coach' : 'Ask your financial coach'}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 20,
          zIndex: 9999,
          width: 54,
          height: 54,
          borderRadius: '50%',
          background: open ? 'var(--surf2)' : 'var(--ylw)',
          border: open ? '1.5px solid var(--bdr)' : 'none',
          cursor: 'pointer',
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,.45)',
          transition: 'background .15s, transform .1s',
          color: open ? 'var(--txt)' : '#000',
        }}
      >
        {open ? '✕' : '🤖'}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 86,
            right: 16,
            width: 'min(380px, calc(100vw - 32px))',
            height: 'min(520px, calc(100dvh - 130px))',
            background: 'var(--surf)',
            border: '1px solid var(--bdr)',
            borderRadius: 18,
            zIndex: 9998,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 12px 48px rgba(0,0,0,.55)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--bdr)',
              background: 'var(--surf2)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 22 }}>🤖</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--txt)' }}>Financial Coach</div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>AI · knows your actual numbers</div>
            </div>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {history.length === 0 && loading && (
              <div style={{ color: 'var(--mut)', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                Loading your numbers...
              </div>
            )}

            {history.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '9px 13px',
                    borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: msg.role === 'user' ? 'var(--ylw)' : 'var(--surf2)',
                    color: msg.role === 'user' ? '#000' : 'var(--txt)',
                    fontSize: 13,
                    lineHeight: 1.55,
                    border: msg.role === 'assistant' ? '1px solid var(--bdr)' : 'none',
                    fontWeight: msg.role === 'user' ? 600 : 400,
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && history.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '9px 13px',
                    borderRadius: '14px 14px 14px 4px',
                    background: 'var(--surf2)',
                    border: '1px solid var(--bdr)',
                    fontSize: 13,
                    color: 'var(--mut)',
                  }}
                >
                  Thinking...
                </div>
              </div>
            )}

            {error && (
              <div style={{ fontSize: 12, color: 'var(--red)', textAlign: 'center', padding: '4px 8px' }}>
                {error}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions — show after greeting, before first user message */}
          {history.length === 1 && !loading && (
            <div
              style={{
                padding: '0 14px 10px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                flexShrink: 0,
              }}
            >
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  style={{
                    fontSize: 11,
                    padding: '5px 11px',
                    borderRadius: 20,
                    border: '1px solid var(--bdr)',
                    background: 'var(--bg)',
                    color: 'var(--txt)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    lineHeight: 1.4,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div
            style={{
              padding: '10px 14px',
              borderTop: '1px solid var(--bdr)',
              display: 'flex',
              gap: 8,
              flexShrink: 0,
              background: 'var(--surf)',
            }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask anything about your finances..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '9px 13px',
                borderRadius: 11,
                border: '1.5px solid var(--bdr)',
                background: 'var(--surf2)',
                color: 'var(--txt)',
                fontSize: 13,
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                border: 'none',
                background: input.trim() && !loading ? 'var(--ylw)' : 'var(--surf2)',
                color: input.trim() && !loading ? '#000' : 'var(--mut)',
                fontWeight: 800,
                fontSize: 18,
                cursor: input.trim() && !loading ? 'pointer' : 'default',
                transition: 'background .15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              ↑
            </button>
          </div>
        </div>
      )}
    </>
  );
}
